import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GitBranch, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const AdminMLM = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({ levels: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/mlm-settings`, {
        headers: getAuthHeaders(),
      });
      setSettings(res.data);
    } catch (error) {
      toast.error("Failed to load MLM settings");
    }
    setLoading(false);
  };

  const addLevel = () => {
    const newLevel = settings.levels.length + 1;
    setSettings({
      ...settings,
      levels: [...settings.levels, { level: newLevel, commission_percent: 0 }],
    });
  };

  const removeLevel = (index) => {
    const newLevels = settings.levels.filter((_, i) => i !== index);
    // Re-number levels
    const renumbered = newLevels.map((l, i) => ({ ...l, level: i + 1 }));
    setSettings({ ...settings, levels: renumbered });
  };

  const updateLevel = (index, commission) => {
    const newLevels = [...settings.levels];
    newLevels[index] = { ...newLevels[index], commission_percent: parseFloat(commission) || 0 };
    setSettings({ ...settings, levels: newLevels });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mlm-settings`, {
        levels: settings.levels,
      }, { headers: getAuthHeaders() });
      toast.success("MLM settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout title="MLM Settings">
        <div className="animate-pulse space-y-4">
          <div className="h-40 bg-gray-200 rounded-2xl" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="MLM Settings">
      <Card className="border-0 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-primary-600" />
            Referral Commission Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 mb-6">
            Configure commission percentages for each referral level. Users earn commission
            based on their referral tree depth.
          </p>

          <div className="space-y-4">
            {settings.levels.map((level, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                  <span className="font-bold text-primary-700">L{level.level}</span>
                </div>
                <div className="flex-1">
                  <Label>Level {level.level} Commission (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={level.commission_percent}
                    onChange={(e) => updateLevel(index, e.target.value)}
                    className="mt-1"
                    data-testid={`mlm-level-${level.level}-input`}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 shrink-0"
                  onClick={() => removeLevel(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}

            {settings.levels.length < 10 && (
              <Button
                variant="outline"
                onClick={addLevel}
                className="w-full"
                data-testid="add-mlm-level-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Level
              </Button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t">
            <Button
              onClick={saveSettings}
              className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
              disabled={saving}
              data-testid="save-mlm-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <h4 className="font-medium text-blue-800 mb-2">How it works</h4>
            <p className="text-sm text-blue-700">
              When a referred user makes a purchase, commissions are distributed up the referral tree.
              Level 1 is the direct referrer, Level 2 is the referrer's referrer, and so on.
            </p>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminMLM;
